import { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SceneInfo } from '../../../../types/scene.types';
import { ElementWithUserNames, ElementInfo, ElementType } from '../../../../types/element.types';
import { getSceneById, getScenesByBreakdownId } from '../../../../api/breakdown';
import {
  getElementsByBreakdownId,
  getElementRelationsByBreakdownID,
} from '../../../../api/element';
// clearBreakdownItemsCache 已移除，缓存更新由 BreakdownDataCacheContext 统一管理
import { useDataCache } from '../../../../contexts/BreakdownDataCacheContext';
import { useTabCache } from '../../../../contexts/TabCacheContext';

/**
 * 场次数据获取 Hook
 * 
 * 功能：
 * - 获取场次详情；场次元素仅在 Item/Relation 缓存缺失时拉取分场级接口并写入（与 loader 并存时不重复请求）
 * - 刷新场次元素列表
 * - 从缓存恢复状态
 * - 处理缓存逻辑
 */
export const useSceneData = (
  sceneId: string,
  visible: boolean,
  setSceneInfo: React.Dispatch<React.SetStateAction<SceneInfo | null>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setSceneLocationDisplayText: React.Dispatch<React.SetStateAction<string>>,
  setAllScenes: React.Dispatch<React.SetStateAction<SceneInfo[]>>,
  setActiveTab: React.Dispatch<React.SetStateAction<ElementType | null>>,
  setLocalPlotDay: React.Dispatch<React.SetStateAction<string>>,
  setPagesNumberInputValue: React.Dispatch<React.SetStateAction<string | undefined>>,
  setLocalSceneCode: React.Dispatch<React.SetStateAction<string>>,
  setIsEditingSceneCode: React.Dispatch<React.SetStateAction<boolean>>,
  setActiveOperationsTab: React.Dispatch<React.SetStateAction<ElementType | null>>,
  setOperationsMenuVisible: React.Dispatch<React.SetStateAction<boolean>>,
  sceneInfo: SceneInfo | null,
  externalAllScenes?: SceneInfo[],
  externalSceneData?: Partial<SceneInfo>,
  refreshTrigger?: boolean | number
) => {
  const { t } = useTranslation();
  const {
    getSceneCache,
    setSceneCache,
    subscribeSceneCache,
    getBreakdownCache,
    subscribeBreakdownCache,
    getElementCache,
    getElementRelationCache,
    setElementCache,
    setElementRelationCache,
  } = useDataCache();
  const { getCache: getTabCache } = useTabCache();

  const loadUserNamesForElements = useCallback(async (elements: ElementInfo[]): Promise<ElementWithUserNames[]> =>
    elements.map(element => ({ ...element })), []);

  // 判断是否是占位行（临时 ID）
  const isPlaceholderScene = (id: string): boolean => {
    return id.startsWith('temp-');
  };

  const fetchData = useCallback(async () => {
    if (!sceneId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // 如果是占位行，从外部数据源获取，不调用API
      // 注意：externalSceneData 和 externalAllScenes 通过闭包访问，不需要作为依赖
      if (isPlaceholderScene(sceneId)) {
        let placeholderData: SceneInfo | null = null;
        
        // 优先从 externalSceneData 获取
        if (externalSceneData && Object.keys(externalSceneData).length > 0) {
          placeholderData = externalSceneData as SceneInfo;
        } 
        // 其次从外部传入的 allScenes 中查找
        else if (externalAllScenes && externalAllScenes.length > 0) {
          placeholderData = externalAllScenes.find(s => s.sceneID === sceneId) || null;
        }
        
        if (placeholderData) {
          setSceneInfo(placeholderData);
          setSceneLocationDisplayText(placeholderData.sceneLocation || '');
          setAllScenes(externalAllScenes || []);
          setLoading(false);
          setError(null);
          return;
        } else {
          // 如果找不到占位数据，显示错误但不调用API
          setError(t('scene.placeholderSceneNotFound'));
          setLoading(false);
          return;
        }
      }
      
      const sceneResponse = await getSceneById(sceneId);
      const scene = sceneResponse.data;
      setSceneInfo(scene);
      setSceneLocationDisplayText(scene.sceneLocation || '');

      // 先写入 Scene 缓存，便于 useElementsData 解析 sceneBelongBreakdownID
      setSceneCache(sceneId, { sceneInfo: scene });

      const belongId = scene.sceneBelongBreakdownID;

      if (belongId) {
        try {
          const useExternalScenes =
            externalAllScenes && externalAllScenes.length > 0;

          // 分场页 / useBreakdownDataLoader 已写入缓存时，不再重复请求元素与关系
          const itemCacheExisting = getElementCache(belongId);
          const relationCacheExisting = getElementRelationCache(belongId);
          const itemRelationCachesPresent =
            itemCacheExisting != null && relationCacheExisting != null;

          if (!itemRelationCachesPresent) {
            const [itemsRes, relRes] = await Promise.all([
              getElementsByBreakdownId(belongId),
              getElementRelationsByBreakdownID(belongId),
            ]);
            const enriched = await loadUserNamesForElements(itemsRes.data || []);
            setElementCache(belongId, { elements: enriched as ElementInfo[] });
            setElementRelationCache(belongId, { elementRelations: relRes.data || [] });
          }

          if (useExternalScenes) {
            setAllScenes(externalAllScenes);
          } else {
            const breakdownBundle = getBreakdownCache(belongId);
            if (breakdownBundle?.scenes && breakdownBundle.scenes.length > 0) {
              setAllScenes(breakdownBundle.scenes as SceneInfo[]);
            } else {
              const allScenesRes = await getScenesByBreakdownId(belongId);
              setAllScenes(allScenesRes.data || []);
            }
          }
        } catch (error) {
          console.warn('加载分场场次元素缓存失败（不影响场次详情主体）:', error);
          setAllScenes([]);
        }
      } else {
        setAllScenes(
          externalAllScenes && externalAllScenes.length > 0
            ? externalAllScenes
            : []
        );
      }

    } catch (error) {
      console.error(t('scene.getSceneDataFailed'), error);
      setError(error instanceof Error ? error.message : t('scene.getSceneDataFailed'));
    } finally {
      setLoading(false);
    }
  }, [
    sceneId,
    externalAllScenes,
    externalSceneData,
    setSceneInfo,
    setLoading,
    setError,
    setSceneLocationDisplayText,
    setAllScenes,
    setSceneCache,
    getElementCache,
    getElementRelationCache,
    getBreakdownCache,
    setElementCache,
    setElementRelationCache,
    loadUserNamesForElements,
    t
  ]);

  const refreshElements = useCallback(async () => {
    if (!sceneId) return;

    const breakdownId =
      getSceneCache(sceneId)?.sceneInfo?.sceneBelongBreakdownID ?? sceneInfo?.sceneBelongBreakdownID;
    if (!breakdownId) {
      console.warn('[useSceneData] refreshElements: 缺少分场 ID，跳过刷新场次元素缓存');
      return;
    }

    try {
      const [itemsRes, relRes] = await Promise.all([
        getElementsByBreakdownId(breakdownId),
        getElementRelationsByBreakdownID(breakdownId),
      ]);
      const enriched = await loadUserNamesForElements(itemsRes.data || []);
      setElementCache(breakdownId, { elements: enriched as ElementInfo[] });
      setElementRelationCache(breakdownId, { elementRelations: relRes.data || [] });
    } catch (error) {
      console.error('刷新场次元素列表失败:', error);
    }
  }, [
    sceneId,
    sceneInfo?.sceneBelongBreakdownID,
    loadUserNamesForElements,
    setElementCache,
    setElementRelationCache,
    getSceneCache,
  ]);

  // 从缓存恢复状态
  const restoreFromCache = useCallback((cachedData: any) => {
    if (cachedData.sceneInfo) {
      setSceneInfo(cachedData.sceneInfo);
    }
    // 注意：不再从缓存恢复 elements
    // elements 现在通过 ElementCache + ElementRelationCache 组装获取
    if (cachedData.sceneLocationDisplayText !== undefined) setSceneLocationDisplayText(cachedData.sceneLocationDisplayText);
    const scenesFromCache = cachedData.allScenes;
    if (scenesFromCache) setAllScenes(scenesFromCache);
    if (cachedData.activeTab !== undefined) setActiveTab(cachedData.activeTab as ElementType | null);
    if (cachedData.localPlotDay !== undefined) setLocalPlotDay(cachedData.localPlotDay);
    // 页数字段的本地输入值不应该从缓存恢复（参考场次表的实现，编辑状态不应该跨会话保存）
    // 总是重置为 undefined，让输入框显示 sceneInfo 的值
    setPagesNumberInputValue(undefined);
    if (cachedData.localSceneCode !== undefined) setLocalSceneCode(cachedData.localSceneCode);
    // 注意：isEditingSceneCode 不从缓存恢复，因为场号输入框是独立的，编辑状态不应该跨会话保存
    // 确保编辑状态始终为 false（普通状态）
    setIsEditingSceneCode(false);
    if (cachedData.activeOperationsTab !== undefined) setActiveOperationsTab(cachedData.activeOperationsTab as ElementType | null);
    if (cachedData.operationsMenuVisible !== undefined) setOperationsMenuVisible(cachedData.operationsMenuVisible);
  }, [
    setSceneInfo,
    setSceneLocationDisplayText,
    setAllScenes,
    setActiveTab,
    setLocalPlotDay,
    setPagesNumberInputValue,
    setLocalSceneCode,
    setIsEditingSceneCode,
    setActiveOperationsTab,
    setOperationsMenuVisible,
  ]);

  // 使用 ref 追踪已初始化的 sceneId，避免重复初始化
  const initializedSceneIdRef = useRef<string | null>(null);
  
  // 初始化数据加载（检查缓存）
  // 只在 sceneId 或 visible 变化时执行，不应该在每次状态更新时执行
  useEffect(() => {
    if (!visible || !sceneId) {
      initializedSceneIdRef.current = null;
      return;
    }
    
    // 如果已经初始化过这个 sceneId，跳过（避免重复初始化）
    if (initializedSceneIdRef.current === sceneId) {
      return;
    }
    
    // 标记为已初始化
    initializedSceneIdRef.current = sceneId;
    
    // 如果是占位行，直接从外部数据源加载，不检查缓存
    if (isPlaceholderScene(sceneId)) {
      fetchData();
      return;
    }
    
    // 检查 BreakdownDataCacheContext 缓存
    const sceneCache = getSceneCache(sceneId);
    const currentTabId = `scene-${sceneId}`;
    const tabCache = getTabCache(currentTabId);
    
    // 若有 Scene 缓存，优先使用；若有 sceneBelongBreakdownID，再从 Breakdown 缓存取最新场次（表格编辑后 flush 会更新）
    let sceneInfoToUse: SceneInfo | null = sceneCache?.sceneInfo || null;
    if (sceneInfoToUse?.sceneBelongBreakdownID) {
      const breakdownCache = getBreakdownCache(sceneInfoToUse.sceneBelongBreakdownID);
      const sceneFromScenesCache = breakdownCache?.scenes?.find(s => s.sceneID === sceneId);
      if (sceneFromScenesCache) {
        sceneInfoToUse = sceneFromScenesCache as SceneInfo;
      }
    }
    
    if (sceneInfoToUse) {
      // 合并业务数据缓存和 UI 状态缓存
      restoreFromCache({
        sceneInfo: sceneInfoToUse,
        ...(tabCache || {}), // 合并 UI 状态缓存
      });
      setLoading(false);
      setError(null);
      return;
    }
    if (sceneCache && !sceneCache.sceneInfo) {
      console.warn('Scene 缓存数据无效，清除缓存', { sceneId, sceneCache });
    }
    
    // 没有缓存或缓存无效，加载数据
    fetchData();
  }, [sceneId, visible, fetchData, getSceneCache, getBreakdownCache, getTabCache, restoreFromCache, setError, setLoading]);
  
  // 订阅 Scene 缓存更新
  useEffect(() => {
    if (!sceneId || !visible) return;
    
    const handleCacheUpdate = () => {
      const sceneCache = getSceneCache(sceneId);
      if (sceneCache?.sceneInfo) {
        setSceneInfo(sceneCache.sceneInfo);
      }
      // 注意：不再从 SceneCache 获取 elements
      // elements 现在通过 ElementCache + ElementRelationCache 组装获取
    };
    
    const unsubscribe = subscribeSceneCache(sceneId, handleCacheUpdate);
    return () => {
      unsubscribe();
    };
  }, [sceneId, visible, getSceneCache, subscribeSceneCache, setSceneInfo]);
  
  // 新增：订阅 Breakdown 缓存更新（当场次表更新时，同步更新场次详情）
  useEffect(() => {
    if (!sceneId || !visible || !sceneInfo?.sceneBelongBreakdownID) return;
    
    const breakdownID = sceneInfo.sceneBelongBreakdownID; // 保存到局部变量，确保类型安全
    
    const handleBreakdownCacheUpdate = () => {
      // 注意：编辑时不会更新缓存，所以订阅者不会被触发
      // 只有完成编辑时（flushSceneUpdate）才会更新缓存并触发订阅者
      const breakdownCache = getBreakdownCache(breakdownID);
      if (breakdownCache?.scenes) {
        const updatedScene = breakdownCache.scenes.find(s => s.sceneID === sceneId);
        if (updatedScene) {
          // 检查是否有实际变化，避免不必要的更新
          setSceneInfo(prev => {
            if (!prev) {
              return updatedScene as SceneInfo;
            }
            
            const hasChanges = Object.keys(updatedScene).some(
              key => prev[key as keyof SceneInfo] !== updatedScene[key as keyof SceneInfo]
            );
            if (!hasChanges) {
              return prev;
            }
            
            // 直接使用 updatedScene，确保使用缓存中的最新数据
            return updatedScene as SceneInfo;
          });
          
          // 如果更新了 sceneLocation 字段，同步更新 sceneLocationDisplayText
          if (updatedScene.sceneLocation !== undefined) {
            setSceneLocationDisplayText(updatedScene.sceneLocation || '');
          }
        }
      }
    };
    
    const unsubscribe = subscribeBreakdownCache(breakdownID, handleBreakdownCacheUpdate);
    return () => {
      unsubscribe();
    };
  }, [sceneId, visible, sceneInfo?.sceneBelongBreakdownID, getBreakdownCache, subscribeBreakdownCache, setSceneInfo, setSceneLocationDisplayText]);

  // 注意：移除了 externalSceneData 的监听更新逻辑
  // externalSceneData 现在仅用于占位行支持（在 fetchData 中处理）
  // 数据同步现在完全通过 BreakdownCache 订阅者机制实现

  // 同步外部传入的所有场次数据（用于推荐数据源）
  useEffect(() => {
    if (externalAllScenes && externalAllScenes.length > 0) {
      setAllScenes(externalAllScenes);
    }
  }, [externalAllScenes, setAllScenes]);

  // 监听刷新触发
  useEffect(() => {
    if (refreshTrigger && sceneId && visible) {
      refreshElements();
    }
  }, [refreshTrigger, sceneId, visible, refreshElements]);

  return {
    fetchData,
    refreshElements,
  };
};
